import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Box, Divider, Grid, InputAdornment, Paper, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { Form, Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';
import _ from 'lodash';

import useNotification from '../../../../../core/hooks/use_notification';
import { getProductAction, getProductState } from '../../../../../features/product/get_product';
import { getConstantsState } from '../../../../../features/product/get_constants';
import TextFieldFormik from '../../../../common/text_field';
import { SubmitButton } from '../../../../common/button';
import { SelectFieldFormik, SelectFieldMultipleFormik } from '../../../../common/select_field';
import RadioGroupFormik from '../../../../common/radio_group';
import { isNullOrUndefined } from '../../../../../core/utils/misc_utils';
import FileInput from '../../../../common/file_input';
import { updateProductAction, updateProductState } from '../../../../../features/product/update_product';
import { createProductAction, createProductState } from '../../../../../features/product/create_product';
import { createProductNotificationData, getProductNotificationData, updateProductNotificationData } from '../../../../../core/utils/notification_utils';
import { getAllFoodPartnersState } from '../../../../../features/food_partner/get_all_food_partners';
import { SuggestedShelfEnum } from '../../../../../core/enums/product/product';
import { yesNoOptions } from '../../../../common/form_utils';
import { getAllProducersState } from '../../../../../features/product/get_all_producers';
import { getAllHeatingProgramsState } from '../../../../../features/product/get_all_heating_programs';
import { getDepositsState } from '../../../../../features/price/get_deposits';
import { getProductDepositState } from '../../../../../features/price/get_product_deposit';
import BottomActionBar from '../../../../common/bottom_action_bar';
import { GET_ROUTE_PRODUCT_FORMS } from '../../../../../core/constants';

const productLabelTypeOptions = [
    { value: 'INTERNAL', label: 'Własna' },
    { value: 'EXTERNAL_WITH_CODE', label: 'Zewnętrzna z kodem' },
    { value: 'EXTERNAL_WITHOUT_CODE', label: 'Zewnętrzna bez kodu' },
];

const suggestedShelfOptions = [
    { value: SuggestedShelfEnum.F1_WRAPS, label: '1L - Wrapy' },
    { value: SuggestedShelfEnum.F2_LUNCHES_OR_WRAPS, label: '2L - Lunche / Wrapy' },
    { value: SuggestedShelfEnum.F3_SALADS_OR_SUSHI, label: '3L - Sałatki / Sushi' },
    { value: SuggestedShelfEnum.F4_BAGELS, label: '4L - Bajgle' },
    { value: SuggestedShelfEnum.F5_SANDWICHES, label: '5L - Kanapki' },
    { value: SuggestedShelfEnum.F6_DAIRY, label: '6L - Nabiał' },
    { value: SuggestedShelfEnum.F7_DRINKS, label: '7L - Napoje szkło' },
    { value: SuggestedShelfEnum.D1_BARS, label: '1S - Batony' },
    { value: SuggestedShelfEnum.D2_CHIPS_OR_MOUSSES, label: '2S - Chrupki / Musy' },
    { value: SuggestedShelfEnum.NOT_SPECIFIED, label: 'Nieokreślona' }
];

const vatOptions = [
    { value: 0, label: '0%' },
    { value: 5, label: '5%' },
    { value: 8, label: '8%' },
    { value: 23, label: '23%' },
];

const MacronutrientsSection = ({ units }) => {
    const { values, setFieldValue, touched, errors, handleBlur } = useFormikContext();

    const [mode, setMode] = useState('total');
    const [local100g, setLocal100g] = useState({ calories: '', carbons: '', protein: '', fat: '' });

    const currentWeight = parseFloat(values.weight) || 0;

    const handleModeChange = (e, newMode) => {
        if (newMode !== null && newMode !== mode) {
            if (newMode === 'per100') {
                if (currentWeight > 0) {
                    setLocal100g({
                        calories: values.calories !== '' ? parseFloat(((values.calories / currentWeight) * 100).toFixed(2)).toString() : '',
                        carbons: values.carbons !== '' ? parseFloat(((values.carbons / currentWeight) * 100).toFixed(2)).toString() : '',
                        protein: values.protein !== '' ? parseFloat(((values.protein / currentWeight) * 100).toFixed(2)).toString() : '',
                        fat: values.fat !== '' ? parseFloat(((values.fat / currentWeight) * 100).toFixed(2)).toString() : '',
                    });
                } else {
                    setLocal100g({ calories: '', carbons: '', protein: '', fat: '' });
                }
            }
            setMode(newMode);
        }
    };

    useEffect(() => {
        if (mode === 'per100' && currentWeight > 0) {
            ['calories', 'carbons', 'protein', 'fat'].forEach(field => {
                const val100 = parseFloat(local100g[field]);
                if (!isNaN(val100)) {
                    setFieldValue(field, parseFloat(((val100 * currentWeight) / 100).toFixed(2)));
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.weight]);

    const handle100gChange = (field) => (e) => {
        const valStr = e.target.value;
        setLocal100g(prev => ({ ...prev, [field]: valStr }));

        const valNum = parseFloat(valStr);
        if (!isNaN(valNum) && currentWeight > 0) {
            const total = (valNum * currentWeight) / 100;
            setFieldValue(field, parseFloat(total.toFixed(2)));
        } else if (valStr === '') {
            setFieldValue(field, '');
        }
    };

    const renderMacroField = (name, label) => {
        if (mode === 'total') {
            return (
                <TextFieldFormik
                    size="small"
                    name={ name }
                    label={ label }
                    type="number"
                    required
                />
            );
        }

        const errorText = touched[name] && errors[name];
        return (
            <TextField
                size="small"
                fullWidth
                name={ `${ name }_100g` }
                label={ label }
                type="number"
                required
                value={ local100g[name] }
                onChange={ handle100gChange(name) }
                onBlur={ () => handleBlur({ target: { name } }) }
                error={ Boolean(errorText) }
                helperText={ errorText || (currentWeight <= 0 ? 'Podaj najpierw wagę' : '') }
                disabled={ currentWeight <= 0 }
                InputProps={ {
                    endAdornment: <InputAdornment position="end">/ 100</InputAdornment>
                } }
            />
        );
    };

    return (
        <Paper variant="outlined" sx={ { p: 3 } }>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={ 2 } flexWrap="wrap" gap={ 2 }>
                <Typography variant="subtitle2" color="textSecondary"
                            sx={ { textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.5px', mb: 0 } }>
                    Waga i Makroskładniki
                </Typography>

                <ToggleButtonGroup
                    value={ mode }
                    exclusive
                    onChange={ handleModeChange }
                    size="small"
                    color="primary"
                >
                    <ToggleButton value="total" sx={ { px: 2, fontSize: '0.8rem' } }>Cała masa</ToggleButton>
                    <ToggleButton value="per100" sx={ { px: 2, fontSize: '0.8rem' } }>W 100 g/ml</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <Grid container spacing={ 2 }>
                <Grid item xs={ 12 } sm={ 6 }>
                    <TextFieldFormik name="weight" label="Waga" type="number" required/>
                </Grid>
                <Grid item xs={ 12 } sm={ 6 }>
                    <SelectFieldFormik
                        name="unitId"
                        label="Jednostka wagi"
                        options={ units }
                        required
                    />
                </Grid>
                <Grid item xs={ 12 }><Divider sx={ { my: 1 } }/></Grid>
                <Grid item xs={ 12 } sm={ 6 } md={ 3 }>
                    { renderMacroField('calories', 'Kalorie') }
                </Grid>
                <Grid item xs={ 12 } sm={ 6 } md={ 3 }>
                    { renderMacroField('carbons', 'Węglowodany') }
                </Grid>
                <Grid item xs={ 12 } sm={ 6 } md={ 3 }>
                    { renderMacroField('protein', 'Białko') }
                </Grid>
                <Grid item xs={ 12 } sm={ 6 } md={ 3 }>
                    { renderMacroField('fat', 'Tłuszcz') }
                </Grid>
            </Grid>
        </Paper>
    );
};

const ProductForm = ({ isEdit }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { showNotification, showErrorMessage } = useNotification();

    const getProduct = useSelector(getProductState);
    const getConstants = useSelector(getConstantsState);
    const getProducers = useSelector(getAllProducersState);
    const getFoodPartners = useSelector(getAllFoodPartnersState);
    const getAllHeatingPrograms = useSelector(getAllHeatingProgramsState);
    const getDeposits = useSelector(getDepositsState);
    const getProductDeposit = useSelector(getProductDepositState);

    const updateProduct = useSelector(updateProductState);
    const createProduct = useSelector(createProductState);

    const [picture, setPicture] = useState(null);

    const initialValues = {
        workingName: getProduct?.data?.workingName || '',
        receiptName: getProduct?.data?.receiptName || '',
        producerId: getProducers?.data?.find(producer => producer?.id === getProduct?.data?.producer?.id)?.id || '',
        typeId: getConstants?.data?.productConstants?.FOOD_TYPE?.find(productConstant => productConstant?.body === getProduct?.data?.type)?.id || '',
        allergenIds: [...(getProduct?.data?.allergens || [])].map(name => getConstants?.data?.productConstants?.ALLERGEN?.find(c => c.body === name)?.id).filter(Boolean),
        possibleAllergenIds: [...(getProduct?.data?.possibleAllergens || [])].map(name => getConstants?.data?.productConstants?.ALLERGEN?.find(c => c.body === name)?.id).filter(Boolean),
        typeCuisineId: getConstants?.data?.productConstants?.TYPE_CUISINE?.find(productConstant => productConstant?.body === getProduct?.data?.typeCuisine)?.id || '',
        dietIds: [...(getProduct?.data?.diets || [])].map(name => getConstants?.data?.productConstants?.DIET?.find(c => c.body === name)?.id).filter(Boolean),
        flavorId: getConstants?.data?.productConstants?.FLAVOR?.find(productConstant => productConstant?.body === getProduct?.data?.flavor)?.id || '',
        attributeIds: [...(getProduct?.data?.attributes || [])].map(name => getConstants?.data?.productConstants?.ATTRIBUTE?.find(c => c.body === name)?.id).filter(Boolean),
        foodPartnerIds: [...(getProduct?.data?.foodPartnerIds || [])],

        defaultDaysToExpiration: getProduct?.data?.defaultDaysToExpiration ?? '',
        weight: getProduct?.data?.weight ?? '',
        unitId: getConstants?.data?.units?.find(unit => unit?.symbol === getProduct?.data?.unit)?.id || '',
        calories: getProduct?.data?.calories ?? '',
        carbons: getProduct?.data?.carbons ?? '',
        protein: getProduct?.data?.protein ?? '',
        fat: getProduct?.data?.fat ?? '',

        productLabelType: productLabelTypeOptions.find(productLabelTypeOption => productLabelTypeOption.value === getProduct?.data?.productLabel)?.value || '',
        productLabelFormatId: getConstants?.data?.labelFormats?.find(labelFormat => labelFormat?.format === getProduct?.data?.productLabelFormat)?.id || '',
        suggestedShelf: suggestedShelfOptions.find(suggestedShelf => suggestedShelf.value === getProduct?.data?.suggestedShelf)?.value || '',

        maxShelfSlots: getProduct?.data?.maxShelfSlots ?? '',
        piecesPerSlot: getProduct?.data?.piecesPerSlot ?? '',
        reservationTopSegmentCapacity: getProduct?.data?.reservationTopSegmentCapacity ?? '',
        reservationStandardSegmentCapacity: getProduct?.data?.reservationStandardSegmentCapacity ?? '',
        reservationBottomSegmentCapacity: getProduct?.data?.reservationBottomSegmentCapacity ?? '',

        recommended: getProduct?.data?.recommended || false,
        heatingProgramId: getProduct?.data?.heatingProgramId || 'none',
        picture: null,

        defaultSalesGrossPrice: '',
        defaultPurchaseNetPrice: '',
        vat: '',
        depositId: 'none',
        currencyCode: 'PLN'
    };

    const schema = Yup.object().shape({
        workingName: Yup.string().required(),
        receiptName: Yup.string().max(80, "Nazwa na paragonie nie może przekraczać 80 znaków").required(),
        producerId: Yup.number(),
        typeId: Yup.number().required(),
        weight: Yup.number().required(),
        unitId: Yup.number().required(),
        calories: Yup.number().required(),
        carbons: Yup.number().required(),
        protein: Yup.number().required(),
        fat: Yup.number().required(),
        productLabelType: Yup.string().required(),
        productLabelFormatId: Yup.number().required(),
        suggestedShelf: Yup.string().required(),
        maxShelfSlots: Yup.number().required(),
        piecesPerSlot: Yup.number().required(),
        reservationTopSegmentCapacity: Yup.number(),
        reservationStandardSegmentCapacity: Yup.number(),
        reservationBottomSegmentCapacity: Yup.number(),
        recommended: Yup.bool().required(),

        defaultSalesGrossPrice: !isEdit ? Yup.number().required() : Yup.mixed().notRequired(),
        defaultPurchaseNetPrice: !isEdit ? Yup.number().required() : Yup.mixed().notRequired(),
        vat: !isEdit ? Yup.number().required() : Yup.mixed().notRequired(),
    });

    const onAttach = (file, formik) => {
        let reader = new FileReader();
        reader.onloadend = () => setPicture(reader.result);
        reader.readAsDataURL(file);

        formik.setFieldValue('picture', file);
    };

    const onRemove = (formik) => {
        formik.setFieldValue('picture', null);
        setPicture(null);
        formik.setFieldValue('deletePicture', true);
    };

    const onSubmit = (values) => {
        const form = _.omit(values, [
            'picture',
            values?.heatingProgramId === 'none' && 'heatingProgramId',
            values?.depositId === 'none' && 'depositId'
        ]);

        const picture = values.picture;

        if (isEdit) {
            const id = getProduct?.data?.id;
            dispatch(updateProductAction({ form, id, picture }))
                .then(response => showNotification(updateProductNotificationData(response)))
                .then(() => {
                    dispatch(getProductAction({ id }))
                        .then(response => showErrorMessage(getProductNotificationData(response)));
                });
        } else {
            dispatch(createProductAction({ form, picture }))
                .then(response => {
                    showNotification(createProductNotificationData(response));
                    const newProductId = response?.payload?.id || response?.payload?.data?.id;
                    if (newProductId) {
                        navigate(GET_ROUTE_PRODUCT_FORMS(newProductId), { replace: true });
                    }
                });
        }
    };

    const TitleTypography = ({ children }) => (
        <Typography variant="subtitle2" color="textSecondary"
                    sx={ { mb: 2, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.5px' } }>
            { children }
        </Typography>
    );

    return (
        <Formik
            initialValues={ initialValues }
            validationSchema={ schema }
            validateOnChange={ false }
            validateOnBlur={ false }
            onSubmit={ onSubmit }
        >
            { (formik) => (
                <Box component={ Form }
                     sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, height: '100%' } }>

                    <Grid container spacing={ 4 } alignItems="flex-start" sx={ { flexGrow: 1, mb: 4 } }>
                        <Grid item xs={ 12 } md={ 5 } lg={ 4 }>
                            <Paper variant="outlined" sx={ { p: 3 } }>
                                <TitleTypography>Zdjęcie produktu</TitleTypography>
                                <Box display="flex" justifyContent="center" mb={ 4 } sx={ {
                                    backgroundColor: 'slate.50',
                                    border: theme => `1px dashed ${ theme.palette.slate[300] }`,
                                    borderRadius: 2,
                                    minHeight: 300,
                                    alignItems: 'center'
                                } }>
                                    { picture || (!formik?.values?.deletePicture && !getProduct?.data?.defaultPicture && getProduct?.data?.pictureUrl) ? (
                                        <img
                                            src={ picture || getProduct?.data?.pictureUrl }
                                            alt="Produkt"
                                            style={ { maxWidth: '100%', maxHeight: 300, objectFit: 'contain' } }
                                        />
                                    ) : (
                                        <Typography color="text.secondary" fontWeight={ 500 }>Brak zdjęcia</Typography>
                                    ) }
                                </Box>
                                <FileInput
                                    label="Wgraj nowe zdjęcie"
                                    text="Dodaj lub przeciągnij plik png/jpg"
                                    errorMessage="Niepoprawny plik. Wymagany png lub jpg."
                                    validate={ file => ['image/png', 'image/jpeg'].includes(file.type) }
                                    isAttached={ !isNullOrUndefined(formik.values.picture) || (!formik?.values?.deletePicture && !getProduct?.data?.defaultPicture && getProduct?.data?.pictureUrl) }
                                    onAttach={ (file) => onAttach(file, formik) }
                                    onRemove={ () => onRemove(formik) }
                                    uploadedFileName="Aktualne zdjęcie produktu"
                                />
                            </Paper>
                        </Grid>

                        <Grid item xs={ 12 } md={ 7 } lg={ 8 }>
                            <Stack spacing={ 3 }>

                                { !isEdit && (
                                    <Paper variant="outlined" sx={ { p: 3, borderColor: 'primary.main', bgcolor: 'primary.50' } }>
                                        <TitleTypography>Dane Sprzedażowe (Cena / VAT)</TitleTypography>
                                        <Grid container spacing={ 2 }>
                                            <Grid item xs={ 12 } sm={ 6 } md={ 3 }>
                                                <TextFieldFormik name="defaultSalesGrossPrice" label="Cena Brutto Sprzedaży" type="number" required/>
                                            </Grid>
                                            <Grid item xs={ 12 } sm={ 6 } md={ 3 }>
                                                <TextFieldFormik name="defaultPurchaseNetPrice" label="Cena Netto Zakupu" type="number" required/>
                                            </Grid>
                                            <Grid item xs={ 12 } sm={ 6 } md={ 3 }>
                                                <SelectFieldFormik
                                                    name="vat"
                                                    label="Stawka VAT"
                                                    options={ vatOptions }
                                                    required
                                                />
                                            </Grid>
                                            <Grid item xs={ 12 } sm={ 6 } md={ 3 }>
                                                <SelectFieldFormik
                                                    name="depositId"
                                                    label="Kaucja"
                                                    options={ [{
                                                        value: 'none',
                                                        label: 'Brak kaucji'
                                                    }, ...getDeposits?.data?.map(deposit => ({
                                                        value: deposit?.id,
                                                        label: deposit?.name
                                                    })) || []] }
                                                />
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                ) }

                                <Paper variant="outlined" sx={ { p: 3 } }>
                                    <TitleTypography>Dane podstawowe</TitleTypography>
                                    <Grid container spacing={ 2 }>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <TextFieldFormik name="workingName" label="Nazwa robocza" required/>
                                        </Grid>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <TextFieldFormik name="receiptName" label="Nazwa na paragonie" required/>
                                        </Grid>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <SelectFieldFormik
                                                name="producerId"
                                                label="Producent"
                                                options={ getProducers?.data?.map(producer => ({
                                                    value: producer?.id,
                                                    label: producer?.name
                                                })) }
                                            />
                                        </Grid>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <SelectFieldFormik
                                                name="typeId"
                                                label="Typ produktu"
                                                options={ getConstants?.data?.productConstants?.FOOD_TYPE?.map(productConstant => ({
                                                    value: productConstant?.id,
                                                    label: productConstant?.body
                                                })) }
                                                required
                                            />
                                        </Grid>
                                        <Grid item xs={ 12 }>
                                            <SelectFieldMultipleFormik
                                                name="foodPartnerIds"
                                                label="Dostawcy - wiele"
                                                options={ getFoodPartners?.data?.map(foodPartner => ({
                                                    value: foodPartner?.id,
                                                    label: foodPartner?.name
                                                })) }
                                                required
                                            />
                                        </Grid>
                                    </Grid>
                                </Paper>

                                <Paper variant="outlined" sx={ { p: 3 } }>
                                    <TitleTypography>Atrybuty i Klasyfikacja</TitleTypography>
                                    <Grid container spacing={ 2 }>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <SelectFieldFormik
                                                name="flavorId"
                                                label="Smak"
                                                options={ getConstants?.data?.productConstants?.FLAVOR?.map(productConstant => ({
                                                    value: productConstant?.id,
                                                    label: productConstant?.body
                                                })) }
                                            />
                                        </Grid>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <SelectFieldFormik
                                                name="typeCuisineId"
                                                label="Typ kuchni"
                                                options={ getConstants?.data?.productConstants?.TYPE_CUISINE?.map(productConstant => ({
                                                    value: productConstant?.id,
                                                    label: productConstant?.body
                                                })) }
                                            />
                                        </Grid>
                                        <Grid item xs={ 12 }>
                                            <SelectFieldMultipleFormik
                                                name="allergenIds"
                                                label="Alergeny - wiele"
                                                options={ getConstants?.data?.productConstants?.ALLERGEN?.map(productConstant => ({
                                                    value: productConstant?.id,
                                                    label: productConstant?.body
                                                })) }
                                            />
                                        </Grid>
                                        <Grid item xs={ 12 }>
                                            <SelectFieldMultipleFormik
                                                name="possibleAllergenIds"
                                                label="Możliwe alergeny - wiele"
                                                options={ getConstants?.data?.productConstants?.ALLERGEN?.map(productConstant => ({
                                                    value: productConstant?.id,
                                                    label: productConstant?.body
                                                })) }
                                            />
                                        </Grid>
                                        <Grid item xs={ 12 }>
                                            <SelectFieldMultipleFormik
                                                name="dietIds"
                                                label="Diety - wiele"
                                                options={ getConstants?.data?.productConstants?.DIET?.map(productConstant => ({
                                                    value: productConstant?.id,
                                                    label: productConstant?.body
                                                })) }
                                            />
                                        </Grid>
                                        <Grid item xs={ 12 }>
                                            <SelectFieldMultipleFormik
                                                name="attributeIds"
                                                label="Tagi - wiele"
                                                options={ getConstants?.data?.productConstants?.ATTRIBUTE?.map(productConstant => ({
                                                    value: productConstant?.id,
                                                    label: productConstant?.body
                                                })) }
                                            />
                                        </Grid>
                                    </Grid>
                                </Paper>

                                <MacronutrientsSection
                                    units={ getConstants?.data?.units?.map(unit => ({
                                        value: unit?.id,
                                        label: unit?.symbol
                                    })) || [] }
                                />

                                <Paper variant="outlined" sx={ { p: 3 } }>
                                    <TitleTypography>Logistyka i Rozmiar</TitleTypography>
                                    <Grid container spacing={ 2 }>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <TextFieldFormik name="defaultDaysToExpiration"
                                                             label="Liczba dni przydatności" type="number"
                                                             inputProps={ { min: '1', step: '1' } }/>
                                        </Grid>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <SelectFieldFormik
                                                name="suggestedShelf"
                                                label="Sugerowana półka"
                                                options={ suggestedShelfOptions }
                                                required
                                            />
                                        </Grid>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <TextFieldFormik name="piecesPerSlot" label="Max. ilość w rzędzie"
                                                             type="number" required
                                                             inputProps={ { min: '1', step: '1' } }/>
                                        </Grid>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <TextFieldFormik name="maxShelfSlots" label="Max. rzędów na półce"
                                                             type="number" required
                                                             inputProps={ { min: '1', step: '1' } }/>
                                        </Grid>

                                        <Grid item xs={ 12 }><Divider sx={ { my: 1 } }/></Grid>

                                        <Grid item xs={ 12 } sm={ 4 }>
                                            <TextFieldFormik size="small" name="reservationTopSegmentCapacity"
                                                             label="Pojemność górnego segmentu" type="number"
                                                             inputProps={ { min: '1', step: '1' } }/>
                                        </Grid>
                                        <Grid item xs={ 12 } sm={ 4 }>
                                            <TextFieldFormik size="small" name="reservationStandardSegmentCapacity"
                                                             label="Pojemność std. segmentu" type="number"
                                                             inputProps={ { min: '1', step: '1' } }/>
                                        </Grid>
                                        <Grid item xs={ 12 } sm={ 4 }>
                                            <TextFieldFormik size="small" name="reservationBottomSegmentCapacity"
                                                             label="Pojemność dolnego segmentu" type="number"
                                                             inputProps={ { min: '1', step: '1' } }/>
                                        </Grid>

                                        <Grid item xs={ 12 }><Divider sx={ { my: 1 } }/></Grid>

                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <SelectFieldFormik size="small" name="productLabelType" label="Typ etykiety"
                                                               options={ productLabelTypeOptions } required/>
                                        </Grid>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <SelectFieldFormik size="small" name="productLabelFormatId"
                                                               label="Format etykiety"
                                                               options={ getConstants?.data?.labelFormats?.map(labelFormat => ({
                                                                   value: labelFormat?.id,
                                                                   label: labelFormat?.format
                                                               })) } required/>
                                        </Grid>
                                    </Grid>
                                </Paper>

                                <Paper variant="outlined" sx={ { p: 3 } }>
                                    <TitleTypography>Dodatkowe opcje</TitleTypography>
                                    <Grid container spacing={ 2 }>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <SelectFieldFormik
                                                name="heatingProgramId"
                                                label="Program podgrzewający"
                                                options={ [{
                                                    value: 'none',
                                                    label: 'Nie wybrano'
                                                }, ...getAllHeatingPrograms?.data?.map(heatingProgram => ({
                                                    value: heatingProgram?.id,
                                                    label: heatingProgram?.name
                                                })) || []] }
                                            />
                                        </Grid>
                                        <Grid item xs={ 12 } sm={ 6 } display="flex" alignItems="center">
                                            <RadioGroupFormik
                                                name="recommended"
                                                label="Polecane"
                                                options={ yesNoOptions }
                                            />
                                        </Grid>

                                        {/* Kaucja - widok tylko do odczytu podczas EDYCJI (edycja we właściwym tabie) */ }
                                        { isEdit && !isNullOrUndefined(getProductDeposit?.data?.name) && (
                                            <>
                                                <Grid item xs={ 12 }><Divider sx={ { my: 1 } }/></Grid>
                                                <Grid item xs={ 12 }>
                                                    <TitleTypography>Kaucja</TitleTypography>
                                                    <Grid container spacing={ 2 }>
                                                        <Grid item xs={ 8 }>
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                label="Nazwa kaucji"
                                                                value={ getProductDeposit?.data?.name }
                                                                disabled
                                                                sx={ { '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.8)' } } }
                                                            />
                                                        </Grid>
                                                        <Grid item xs={ 4 }>
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                label="Wartość"
                                                                type="number"
                                                                value={ getProductDeposit?.data?.amount }
                                                                disabled
                                                                sx={ { '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.8)' } } }
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </>
                                        ) }
                                    </Grid>
                                </Paper>
                            </Stack>
                        </Grid>
                    </Grid>

                    <BottomActionBar>
                        <SubmitButton isLoading={ updateProduct?.loading || createProduct?.loading }>
                            { isEdit ? 'Zapisz zmiany w produkcie' : 'Zapisz i utwórz produkt' }
                        </SubmitButton>
                    </BottomActionBar>

                </Box>
            ) }
        </Formik>
    );
};

export default ProductForm;